import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';
import type { TableCellKey } from '../../stores/tableCellStore';
import { useDiagramStore } from '../../stores/diagramStore';
import { computeDockedLayoutUpdatesForTable } from '../../domains/diagram/shapes/table/tableDockLayout.utils';
import { Tracker } from '../../domains/analytics';
import { TABLE_ADVANCED } from '../../domains/analytics/constants/MetricEvents';

type MergeMode = 'merge' | 'unmerge';

export interface ToggleTableMergeParams {
  mode: MergeMode;
  cellKeysByNodeId: Record<string, TableCellKey[]>;
}

type MergedRegion = { startRow: number; startCol: number; endRow: number; endCol: number };

const parseKey = (k: string) => {
  const [rStr, cStr] = k.split(',');
  const r = Number(rStr);
  const c = Number(cStr);
  if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
  return { r, c };
};

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const overlaps = (a: MergedRegion, b: MergedRegion) => {
  const rowOverlap = a.startRow <= b.endRow && b.startRow <= a.endRow;
  const colOverlap = a.startCol <= b.endCol && b.startCol <= a.endCol;
  return rowOverlap && colOverlap;
};

const containsCell = (r: MergedRegion, row: number, col: number) => {
  return row >= r.startRow && row <= r.endRow && col >= r.startCol && col <= r.endCol;
};

const getBoundsFromKeys = (keys: TableCellKey[]) => {
  const uniq = Array.from(new Set(keys));
  const parsed = uniq.map((k) => ({ k, p: parseKey(k) })).filter((x) => !!x.p) as Array<{ k: TableCellKey; p: { r: number; c: number } }>;
  if (parsed.length === 0) return null;

  let rMin = parsed[0].p.r;
  let rMax = parsed[0].p.r;
  let cMin = parsed[0].p.c;
  let cMax = parsed[0].p.c;
  for (const it of parsed) {
    rMin = Math.min(rMin, it.p.r);
    rMax = Math.max(rMax, it.p.r);
    cMin = Math.min(cMin, it.p.c);
    cMax = Math.max(cMax, it.p.c);
  }

  return { parsed, rMin, rMax, cMin, cMax };
};

const isFullRectangle = (keys: TableCellKey[], rMin: number, rMax: number, cMin: number, cMax: number) => {
  const set = new Set(keys);
  const expected = (rMax - rMin + 1) * (cMax - cMin + 1);
  if (set.size !== expected) return false;
  for (let r = rMin; r <= rMax; r += 1) {
    for (let c = cMin; c <= cMax; c += 1) {
      if (!set.has(`${r},${c}` as TableCellKey)) return false;
    }
  }
  return true;
};

export class ToggleTableMergeCommand extends BaseNodeCommand {
  constructor(private nodeIds: string[], private params: ToggleTableMergeParams) {
    super('Toggle Table Merge', params.mode);
  }

  protected doExecute(): NodeChangeRecord[] {
    const mutationManager = this.getMutationManager() as any;
    const records: NodeChangeRecord[] = [];

    const batch: Array<{ nodeId: string; changes: any }> = [];

    for (const nodeId of this.nodeIds) {
      const node = mutationManager.getNode(nodeId) as any;
      if (!node) continue;
      if (node.type !== 'table') continue;

      const keys = this.params.cellKeysByNodeId[nodeId] ?? [];
      if (keys.length === 0) continue;

      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const rowCount = clampCount(attrs.rowCount, 3);
      const colCount = clampCount(attrs.columnCount, 3);
      const showHeader = attrs.showHeader !== false;
      const totalRows = rowCount + (showHeader ? 1 : 0);

      const bounds = getBoundsFromKeys(keys);
      if (!bounds) continue;
      const { rMin, rMax, cMin, cMax } = bounds;

      if (rMin < 0 || cMin < 0) continue;
      if (rMax >= totalRows || cMax >= colCount) continue;

      const mergedRegions = (Array.isArray((attrs as any).mergedRegions) ? ((attrs as any).mergedRegions as MergedRegion[]) : []) ?? [];

      if (this.params.mode === 'merge') {
        const mergeKeys = showHeader && rMin === 0 && rMax > 0
          ? keys.filter((k) => parseKey(k)?.r === 0)
          : keys;
        const mergeBounds = getBoundsFromKeys(mergeKeys);
        if (!mergeBounds) continue;
        const { rMin: mrMin, rMax: mrMax, cMin: mcMin, cMax: mcMax } = mergeBounds;

        if (mergeKeys.length < 2) continue;
        if (!isFullRectangle(mergeKeys, mrMin, mrMax, mcMin, mcMax)) continue;

        const nextRegion: MergedRegion = { startRow: mrMin, startCol: mcMin, endRow: mrMax, endCol: mcMax };
        const hasOverlap = mergedRegions.some((r) => overlaps(r, nextRegion));
        if (hasOverlap) continue;

        const nextMerged = [...mergedRegions, nextRegion];

        const prevText = (((attrs as any).cellText ?? {}) as Record<string, string>) ?? {};
        const prevStyles = (((attrs as any).cellStyles ?? {}) as Record<string, unknown>) ?? {};
        const nextText = { ...prevText };
        const nextStyles = { ...prevStyles };

        for (let r = mrMin; r <= mrMax; r += 1) {
          for (let c = mcMin; c <= mcMax; c += 1) {
            if (r === mrMin && c === mcMin) continue;
            delete nextText[`${r},${c}`];
            delete nextStyles[`${r},${c}`];
          }
        }

        batch.push({
          nodeId,
          changes: {
            attrs: {
              mergedRegions: nextMerged,
              cellText: nextText,
              cellStyles: nextStyles,
            },
          },
        });
        const previewAttrs = { ...(attrs as any), mergedRegions: nextMerged };
        const preview: any = { ...node, attrs: previewAttrs };
        const dockedUpdates = computeDockedLayoutUpdatesForTable(preview, useDiagramStore.getState().nodes);
        dockedUpdates.forEach((u) => batch.push(u));
        continue;
      }

      const selectedCells = bounds.parsed.map((p) => p.p);
      const remove = mergedRegions.filter((mr) => selectedCells.some((c) => containsCell(mr, c.r, c.c)));
      if (remove.length === 0) continue;

      const nextMerged = mergedRegions.filter((mr) => !remove.includes(mr));

      batch.push({
        nodeId,
        changes: {
          attrs: {
            mergedRegions: nextMerged,
          },
        },
      });
      const previewAttrs = { ...(attrs as any), mergedRegions: nextMerged };
      const preview: any = { ...node, attrs: previewAttrs };
      const dockedUpdates = computeDockedLayoutUpdatesForTable(preview, useDiagramStore.getState().nodes);
      dockedUpdates.forEach((u) => batch.push(u));
    }

    if (batch.length > 1 && typeof mutationManager.applyBatchNodeChanges === 'function') {
      const batchRecords = mutationManager.applyBatchNodeChanges(batch);
      if (batchRecords && batchRecords.length > 0) {
        if (this.params.mode === 'merge') {
          Tracker.track(TABLE_ADVANCED.CELLS_MERGED, {
            tableCount: this.nodeIds.length,
          });
        } else {
          Tracker.track(TABLE_ADVANCED.CELLS_UNMERGED, {
            tableCount: this.nodeIds.length,
          });
        }
      }
      return batchRecords ?? [];
    }
    if (batch.length === 1) {
      const rec = mutationManager.applyNodeChange(batch[0].nodeId, batch[0].changes);
      if (rec) {
        if (this.params.mode === 'merge') {
          Tracker.track(TABLE_ADVANCED.CELLS_MERGED, {
            tableCount: 1,
          });
        } else {
          Tracker.track(TABLE_ADVANCED.CELLS_UNMERGED, {
            tableCount: 1,
          });
        }
        records.push(rec);
      }
      return records;
    }

    return records;
  }
}

